import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./slider.css";

type BreakpointConfig = {
  mobileMaxWidth?: number; // default 768
  slidesToShowMobile?: number; // default 1
  slidesToShowDesktop?: number; // default 3
};

export type SliderProps<T> = {
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;

  className?: string;

  // options
  showArrows?: boolean;
  showDots?: boolean;
  loop?: boolean;

  // layout
  gap?: number; // px
  slidesToShow?: number; // fixed for all sizes (optional)
  breakpoint?: BreakpointConfig;

  scrollBy?: number; // default 1

  // autoplay
  autoPlay?: boolean;
  autoPlayIntervalMs?: number;

  // touch/swipe
  swipe?: boolean;
  swipeThresholdPx?: number;

  // icons
  arrowIconUrl?: string;
  arrowDirection?: "left" | "right"; // your svg points left => "left"

  // accessibility labels
  ariaLabels?: {
    prev?: string;
    next?: string;
    pagination?: string;
    goToSlide?: string;
  };
};

const clamp = (n: number, min: number, max: number) =>
  Math.max(min, Math.min(max, n));

export function Slider<T>({
  items,
  renderItem,
  className = "",

  showArrows = true,
  showDots = true,
  loop = true,

  gap = 20,
  slidesToShow,
  breakpoint,

  scrollBy = 1,

  autoPlay = false,
  autoPlayIntervalMs = 3500,

  swipe = true,
  swipeThresholdPx = 40,

  arrowIconUrl = "/public/assets/icons/arrow.svg",
  arrowDirection = "left",
  ariaLabels,
}: SliderProps<T>) {
  const { i18n } = useTranslation();
  const mobileMaxWidth = breakpoint?.mobileMaxWidth ?? 768;
  const lang = String(i18n.language || "").toLowerCase();
  const isUkrainian = lang.startsWith("ua") || lang.startsWith("uk");

  const prevAriaLabel =
    ariaLabels?.prev ?? (isUkrainian ? "Попередній слайд" : "Previous slide");
  const nextAriaLabel =
    ariaLabels?.next ?? (isUkrainian ? "Наступний слайд" : "Next slide");
  const paginationAriaLabel =
    ariaLabels?.pagination ?? (isUkrainian ? "Пагінація" : "Pagination");
  const goToSlideAriaLabel =
    ariaLabels?.goToSlide ??
    (isUkrainian ? "Перейти до слайду" : "Go to slide");

  const [isMobile, setIsMobile] = useState(false);

  const [internalIndex, setInternalIndex] = useState(0);
  const [withTransition, setWithTransition] = useState(true);

  const autoplayTimer = useRef<number | null>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const dragDx = useRef(0);

  const viewportRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const calc = () => setIsMobile(window.innerWidth <= mobileMaxWidth);
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, [mobileMaxWidth]);

  const showCount = useMemo(() => {
    if (slidesToShow && slidesToShow > 0) return slidesToShow;
    const m = breakpoint?.slidesToShowMobile ?? 1;
    const d = breakpoint?.slidesToShowDesktop ?? 3;
    return isMobile ? m : d;
  }, [
    slidesToShow,
    breakpoint?.slidesToShowDesktop,
    breakpoint?.slidesToShowMobile,
    isMobile,
  ]);

  const step = useMemo(
    () => Math.max(1, Math.min(scrollBy, Math.max(1, items.length))),
    [scrollBy, items.length]
  );

  const cloneCount = useMemo(() => {
    if (!loop) return 0;
    return Math.min(items.length, showCount + step);
  }, [loop, items.length, showCount, step]);

  const extended = useMemo(() => {
    if (!items.length) return [];
    if (!loop || cloneCount === 0) return items;

    const head = items.slice(0, cloneCount);
    const tail = items.slice(Math.max(0, items.length - cloneCount));
    return [...tail, ...items, ...head];
  }, [items, loop, cloneCount]);

  useEffect(() => {
    if (!items.length) return;
    const start = loop ? cloneCount : 0;
    setWithTransition(false);
    setInternalIndex(start);
    requestAnimationFrame(() => setWithTransition(true));
  }, [items.length, loop, cloneCount, showCount]);

  const pauseAutoPlay = () => {
    if (autoplayTimer.current) window.clearInterval(autoplayTimer.current);
    autoplayTimer.current = null;
  };
  const resumeAutoPlay = () => {
    if (!autoPlay) return;
    if (autoplayTimer.current) return;
    autoplayTimer.current = window.setInterval(() => {
      goNext();
    }, autoPlayIntervalMs);
  };

  useEffect(() => {
    pauseAutoPlay();
    resumeAutoPlay();
    return () => pauseAutoPlay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlay, autoPlayIntervalMs, step, showCount, items.length, loop]);

  const maxInternal = useMemo(() => {
    // last allowed index for extended array
    return Math.max(0, extended.length - 1);
  }, [extended.length]);

  const canPrev = loop || internalIndex > 0;
  const canNext = loop || internalIndex < maxInternal;

  const goPrev = () => {
    if (!items.length) return;
    setInternalIndex((i) => clamp(i - step, 0, maxInternal));
  };

  const goNext = () => {
    if (!items.length) return;
    setInternalIndex((i) => clamp(i + step, 0, maxInternal));
  };

  const onTransitionEnd = () => {
    if (!loop || cloneCount === 0) return;

    const realStart = cloneCount;
    const realEndExclusive = cloneCount + items.length;

    if (internalIndex >= realEndExclusive) {
      const newIndex = internalIndex - items.length;
      setWithTransition(false);
      setInternalIndex(newIndex);
      requestAnimationFrame(() => setWithTransition(true));
    } else if (internalIndex < realStart) {
      const newIndex = internalIndex + items.length;
      setWithTransition(false);
      setInternalIndex(newIndex);
      requestAnimationFrame(() => setWithTransition(true));
    }
  };

  const dotsCount = useMemo(() => {
    if (!items.length) return 0;
    return Math.ceil(items.length / step);
  }, [items.length, step]);

  const activeDot = useMemo(() => {
    if (!items.length) return 0;

    const realStart = loop ? cloneCount : 0;
    const realIndex =
      (((internalIndex - realStart) % items.length) + items.length) %
      items.length;

    return Math.floor(realIndex / step);
  }, [internalIndex, items.length, loop, cloneCount, step]);

  const goToDot = (dotIdx: number) => {
    if (!items.length) return;

    const targetRealIndex = dotIdx * step; // 0, step, 2*step...
    const realStart = loop ? cloneCount : 0;

    setInternalIndex(realStart + targetRealIndex);
  };

  // swipe
  const onTouchStart = (clientX: number) => {
    if (!swipe) return;
    isDragging.current = true;
    startX.current = clientX;
    dragDx.current = 0;
    pauseAutoPlay();
  };

  const onTouchMove = (clientX: number) => {
    if (!swipe) return;
    if (!isDragging.current) return;
    dragDx.current = clientX - startX.current;
  };

  const onTouchEnd = () => {
    if (!swipe) return;
    if (!isDragging.current) return;

    isDragging.current = false;
    const dx = dragDx.current;

    if (Math.abs(dx) >= swipeThresholdPx) {
      if (dx < 0) goNext();
      else goPrev();
    }

    dragDx.current = 0;
    resumeAutoPlay();
  };

  if (!items?.length) return null;

  const translate = `translate3d(calc(${-internalIndex} * (100% / ${showCount})), 0, 0)`;

  return (
    <div className={`oc-slider ${className}`}>
      <div className="oc-slider__top">
        {showArrows && (
          <button
            className={`oc-slider__arrow oc-slider__arrow--left ${
              !canPrev ? "is-disabled" : ""
            }`}
            onClick={goPrev}
            type="button"
            aria-label={prevAriaLabel}
            disabled={!canPrev}
          >
            <span
              className={`oc-slider__arrowIcon ${
                arrowDirection === "left"
                  ? "oc-slider__arrowIcon--leftBase"
                  : "oc-slider__arrowIcon--rightBase"
              } oc-slider__arrowIcon--left`}
              style={{
                WebkitMaskImage: `url(${arrowIconUrl})`,
                maskImage: `url(${arrowIconUrl})`,
              }}
            />
          </button>
        )}

        <div
          ref={viewportRef}
          className="oc-slider__viewport"
          onTouchStart={(e) => onTouchStart(e.touches[0].clientX)}
          onTouchMove={(e) => onTouchMove(e.touches[0].clientX)}
          onTouchEnd={onTouchEnd}
        >
          <div
            className={`oc-slider__track ${withTransition ? "is-anim" : ""}`}
            style={
              {
                transform: translate,
                ["--showCount" as any]: showCount,
                ["--gap" as any]: `${gap}px`,
              } as any
            }
            onTransitionEnd={onTransitionEnd}
          >
            {extended.map((it, idx) => (
              <div
                key={idx}
                className="oc-slider__slide"
                style={
                  {
                    ["--showCount" as any]: showCount,
                    ["--gap" as any]: `${gap}px`,
                  } as any
                }
              >
                {renderItem(it, idx)}
              </div>
            ))}
          </div>
        </div>

        {showArrows && (
          <button
            className={`oc-slider__arrow oc-slider__arrow--right ${
              !canNext ? "is-disabled" : ""
            }`}
            onClick={goNext}
            type="button"
            aria-label={nextAriaLabel}
            disabled={!canNext}
          >
            <span
              className={`oc-slider__arrowIcon ${
                arrowDirection === "left"
                  ? "oc-slider__arrowIcon--leftBase"
                  : "oc-slider__arrowIcon--rightBase"
              } oc-slider__arrowIcon--right`}
              style={{
                WebkitMaskImage: `url(${arrowIconUrl})`,
                maskImage: `url(${arrowIconUrl})`,
              }}
            />
          </button>
        )}
      </div>

      {showDots && dotsCount > 1 && (
        <div
          className="oc-slider__dots"
          role="tablist"
          aria-label={paginationAriaLabel}
        >
          {Array.from({ length: dotsCount }).map((_, i) => (
            <button
              key={i}
              type="button"
              className={`oc-slider__dot ${i === activeDot ? "is-active" : ""}`}
              onClick={() => goToDot(i)}
              aria-label={`${goToSlideAriaLabel} ${i + 1}`}
              aria-current={i === activeDot}
            />
          ))}
        </div>
      )}
    </div>
  );
}
